import React, { useCallback, useEffect, useState } from 'react';
import type { Dispatch } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import qs from 'query-string';
import { connect } from 'react-redux';

import Helmet from 'wanderu-component-helmet';
import { TripRefreshModal } from 'wanderu-components';
import { userInfoSelectors } from 'wanderu-ducks-account';
import { liveCheckItineraryAction } from 'wanderu-ducks-live-check';
import {
    partnerSelectors,
} from 'wanderu-ducks-partner-data';
import {
    fetchCarrierData,
    fetchTripsAction,
    liveCheckItinerary,
    refreshLiveCheckItinerary,
    removeTripsWithId,
    searchSelectors,
    tripSummaryActions,
    tripSummarySelectors,
} from 'wanderu-ducks-search';
import { splitTestSelectors } from 'wanderu-ducks-split-test';
import {
    fetchTripItineraryAction,
    tripSelectors,
} from 'wanderu-ducks-trips';
import {
    selectCurrency,
    selectLocale,
} from 'wanderu-i18n';
import {
    useDebounce,
    getItineraryId,
} from 'wanderu-utils';

import TripSummaryComponent from '../../components/TripSummary/TripSummary';
import searchConfig from '../../config/tripsummary.locale';
import {
    DEPART_DIRECTION,
    RETURN_DIRECTION,
} from '../../constants';

import type { Trip, TripPrice } from '../../components/TripSummary/types';
import type { RouteComponentProps } from 'react-router-dom';

interface TripSummaryStateProps {
    accountEmail?: string;
    affiliateId?: string;
    allTripIds: string[];
    carrierProviderGroup?: string;
    currency: string;
    deeplinkRoundtrip: boolean;
    destinationPlaceId?: string;
    expiresAt?: number;
    fareVersion?: string;
    forceSessionReplay?: string;
    identityId?: string;
    itineraryId?: string;
    liveCheckData?: {
        error?: string[];
        nativeBookingEnabled?: string[];
        notFound?: string[];
        outboundTripIds?: string[];
        priceDown?: string[];
        priceUp?: string[];
        showLiveCheckMessage?: boolean;
        total?: number;
        unavailable?: string[];
    };
    liveCheckItinerary: (tripIds: string[], options: unknown) => void;
    liveChecksCompleted: boolean;
    liveChecksHaveAlreadyExpired: boolean;
    liveChecksInProgress: boolean;
    locale: string;
    originPlaceId?: string;
    outboundArrive?: {
        cityName?: string;
        localizedDateTime?: string;
        region?: string;
        wcityid?: string;
    };
    outboundDepart?: {
        cityName?: string;
        localizedDateTime?: string;
        region?: string;
        wcityid?: string;
    };
    outboundTripError: boolean;
    outboundTripId: string;
    outboundTripIds: string[];
    outboundTrips: {
        localizedDepartDateTime?: string;
    }[];
    refreshLiveCheckItinerary: (tripIds: string[], options: unknown) => void;
    removeTrip: (tripId: string) => void;
    removeTripIds: () => void;
    requestedSeats: number;
    requestedWheelchairs: number;
    resetLiveCheckItinerary: () => void;
    resetTravelerInfo: () => void;
    returnTripError: boolean;
    returnTripId?: string;
    returnTripIds: string[];
    returnTrips: {
        localizedDepartDateTime?: string;
    }[];
    searchQueries: {
        departDate?: string;
        returnDate?: string;
    };
    seatInfo: {
        maxSeats: number;
        maxWheelchairs: number;
        nonAdaCarriers: string[];
    };
    seats: number;
    setDepartTripId: (tripId: string) => void;
    setReturnTripId: (tripId: string) => void;
    setTravelerInfo: (info: { seats?: number; wheelchairs?: number }) => void;
    showSingleBookButton: boolean;
    showTotalPrice: boolean;
    trackingId?: string;
    tripFareNames: Record<string, string>;
    tripItineraryId?: string;
    tripPrices: TripPrice[];
    tripsAreLoaded: boolean;
    urlDepartFilterParams: Record<string, unknown>;
    urlReturnFilterParams: Record<string, unknown>;
    wheelchairs: number;
    fetchCarrierData: (tripId: string) => Promise<unknown>;
    fetchTrip: (tripId: string) => Promise<unknown>;
    fetchTripItinerary: (tripId: string) => Promise<unknown>;
}

type TripSummaryOwnProps = RouteComponentProps<{
    outboundTripId: string;
    returnTripId?: string;
}>

type TripSummaryProps = TripSummaryOwnProps & TripSummaryStateProps;

const TripSummary = (props: TripSummaryProps) => {
    const {
        affiliateId,
        allTripIds,
        expiresAt,
        fareVersion,
        fetchTrip,
        forceSessionReplay = '',
        itineraryId,
        liveChecksCompleted,
        liveChecksHaveAlreadyExpired,
        liveChecksInProgress,
        outboundTripError,
        outboundTripId,
        removeTrip,
        removeTripIds,
        requestedSeats,
        requestedWheelchairs,
        returnTripError,
        returnTripId,
        seats,
        setDepartTripId,
        setReturnTripId,
        setTravelerInfo,
        trackingId,
        tripsAreLoaded,
        urlDepartFilterParams,
        urlReturnFilterParams,
        wheelchairs,
    } = props;

    const [forceModalClose, setForceModalClose] = useState(false);

    const debouncedSeats = useDebounce(seats, 500);

    const handleLiveCheckItinerary = useCallback(() => {
        return props?.liveCheckItinerary(allTripIds, {
            affiliateId,
            fareVersion,
            itineraryId,
            priceAcknowledged: true,
            seats: debouncedSeats,
            trackingId,
            tripIds: allTripIds,
            wheelchairs,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        // eslint-disable-next-line max-len
        props?.liveCheckItinerary, allTripIds, affiliateId, debouncedSeats, fareVersion, itineraryId, trackingId, wheelchairs]);

    const refreshLiveCheck = useCallback(() => {
        props?.refreshLiveCheckItinerary(allTripIds, {
            affiliateId,
            fareVersion,
            itineraryId,
            seats: debouncedSeats,
            trackingId,
            tripIds: allTripIds,
            wheelchairs,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        // eslint-disable-next-line max-len
        props?.refreshLiveCheckItinerary, allTripIds, affiliateId, debouncedSeats, fareVersion, itineraryId, trackingId, wheelchairs]);

    useEffect(() => {
        if (forceSessionReplay === 'sessionReplay') {
            // starts session replay for all users segmented in to RUM for
            // DataDog in packages/wanderu-shellapp/src/entries/client.jsx
            // datadogRum.init
            datadogRum.startSessionReplayRecording({ force: true });
        }

        // for the case when a user refreshes booking and then navigates back
        setDepartTripId(outboundTripId);

        if (returnTripId) {
            setReturnTripId(returnTripId);
        }

        if (outboundTripError || returnTripError) {
            return undefined;
        }

        if (!tripsAreLoaded) {
            const tripIds = [
                outboundTripId,
                returnTripId,
            ].filter(Boolean) as string[];

            const promises = tripIds.map(async (tripId) => {
                await fetchTrip(tripId);
                await props.fetchCarrierData(tripId);
            });

            Promise.all(promises);
            return undefined;
        }

        if (liveChecksHaveAlreadyExpired) {
            return refreshLiveCheck();
        }

        return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        return () => {
            if (seats !== requestedSeats || wheelchairs !== requestedWheelchairs) {
                setTravelerInfo({
                    seats,
                    wheelchairs,
                });
            }
            removeTripIds();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const haveNotLiveChecked = !liveChecksCompleted && !liveChecksInProgress;

        if ((haveNotLiveChecked) && tripsAreLoaded) {
            handleLiveCheckItinerary();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSeats, tripsAreLoaded, wheelchairs]); // including handleLiveCheckItinerary causes unnecessary re-renders because of the allTripIds dependency

    useEffect(() => {
        if (liveChecksHaveAlreadyExpired) {
            refreshLiveCheck();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveChecksHaveAlreadyExpired]); // including refreshLiveCheck causes unnecessary re-renders because of the allTripIds dependency

    const closeOtherModals = () => {
        setForceModalClose(true);
        setTimeout(() => {
            setForceModalClose(false);
        }, 100);
    };

    const urlFilterParams = {
        ...urlDepartFilterParams,
        ...urlReturnFilterParams,
    };

    return (
        <>
            <Helmet
                supportedLocales={searchConfig.supportedLocales}
                noFollow
                noIndex
            />
            {!liveChecksHaveAlreadyExpired && (
                <TripRefreshModal
                    funnelStage="trip_summary"
                    onPrompt={closeOtherModals}
                    onRefresh={refreshLiveCheck}
                    translationString="trip-refresh"
                    tripsExpireAt={expiresAt}
                />
            )}
            {(tripsAreLoaded || outboundTripError || returnTripError) && (
                <TripSummaryComponent
                    {...props}
                    forceModalClose={forceModalClose}
                    onUnavailableReturnClick={removeTrip}
                    urlFilterParams={urlFilterParams}
                />
            )}
        </>
    );
};

TripSummary.serverFetchData = async (opts: {
    dispatch: Dispatch<unknown>;
    params: { outboundTripId?: string; returnTripId?: string };
    query?: { pax?: string };
}) => {
    const {
        dispatch,
        params,
    } = opts;

    const outboundTripId = decodeURIComponent(params.outboundTripId ?? '');
    const returnTripId = decodeURIComponent(params.returnTripId ?? '');
    const tripIds = [
        outboundTripId,
        returnTripId,
    ].filter(Boolean);

    if (opts?.query?.pax) {
        dispatch(tripSummaryActions.setTravelerInfo({
            seats: opts.query.pax,
        }));
    }
    dispatch(tripSummaryActions.setDepartTripId(outboundTripId));

    if (returnTripId) {
        dispatch(tripSummaryActions.setReturnTripId(returnTripId));
    }

    const promises = tripIds.map(async (tripId) => {
        await dispatch(fetchTripsAction.fetchTrip(tripId));
        await dispatch(fetchCarrierData(tripId));
    });

    await Promise.all(promises);
};

// Define the RootState type based on the Redux store structure
interface RootState {
    i18n: {
        currency: string;
        locale: string;
        supportedCurrencies: [];
        supportedLocales: [];
    };
    // Add other state slices as needed
}

const mapStateToProps = (state: RootState, ownProps: TripSummaryOwnProps) => {
    let {
        outboundTripId,
        returnTripId,
    } = ownProps.match.params;
    const searchQueries = qs.parse(ownProps.location.search) || {};

    outboundTripId = decodeURIComponent(outboundTripId);

    if (returnTripId) {
        returnTripId = decodeURIComponent(returnTripId);
    }

    const tripItineraryId = [outboundTripId, returnTripId].filter(Boolean).join('||');
    const outboundTrips = tripSelectors.selectTripSet(state, outboundTripId) || [];
    const returnTrips = tripSelectors.selectTripSet(state, returnTripId) || [];
    const outboundTripError = fetchTripsAction.selectNotFound(state, outboundTripId);
    const returnTripError = fetchTripsAction.selectNotFound(state, returnTripId);

    if (outboundTripError || returnTripError) {
        return {
            outboundTripError,
            outboundTripId,
            returnTripError,
            returnTripId,
            searchQueries,
        };
    }

    const tripsAreLoaded = !!outboundTrips.length && (!returnTripId || !!returnTrips.length);

    if (!outboundTrips.length && !returnTrips.length) {
        return {
            outboundTripId,
            returnTripId,
            tripsAreLoaded,
        };
    }

    const outboundTripIds = outboundTrips.map((trip: Trip) => trip.tripId);
    const returnTripIds = returnTrips.map((trip: Trip) => trip.tripId);
    const allTripIds = [...outboundTripIds, ...returnTripIds];

    // Seat Data
    const seatInfo = tripSummarySelectors.selectSeatInformation(state, allTripIds);
    const travelerInfo = tripSummarySelectors.selectTravelerInfo(state);
    const requestedWheelchairs = travelerInfo?.wheelchairs;
    const seats = Math.min(travelerInfo?.seats, seatInfo?.maxSeats);
    const wheelchairs = Math.min(requestedWheelchairs, seatInfo?.maxWheelchairs);

    // Trip Data
    const outboundArrive = tripSelectors.selectTripArriveData(state, outboundTripId);
    const outboundDepart = tripSelectors.selectTripDepartData(state, outboundTripId);
    const returnDepart = tripSelectors.selectTripDepartData(state, returnTripId);

    // Context
    const partnerDetails = partnerSelectors.selectPartnerDetails(state);
    const showTotalPrice = partnerDetails?.affiliateId === 'google';

    const tripPrices = tripSummarySelectors.selectPrices(
        state,
        [outboundTripId, returnTripId].filter(Boolean) as string[],
        seats,
        wheelchairs,
        showTotalPrice,
    ) || [];

    const itineraryId = getItineraryId(outboundTripIds, returnTripIds);
    const carrierProviderGroup = tripSummarySelectors.selectServiceAttribution(
        state,
        allTripIds,
    );

    // Livecheck Data
    const lcOpts = { seats, wheelchairs };
    const liveCheckData = tripSummarySelectors.selectLiveCheckData(state, allTripIds, lcOpts);
    const liveChecksInProgress = tripSummarySelectors.selectLiveChecksInProgress(
        state,
        allTripIds,
        lcOpts,
    );
    const liveChecksCompleted = tripSummarySelectors.selectLiveChecksCompleted(
        state,
        allTripIds,
        lcOpts,
    );
    const earliestExpiresAt = tripSummarySelectors.selectLiveCheckEarliestExpiresAt(
        state,
        allTripIds,
        lcOpts,
    );
    const liveChecksHaveAlreadyExpired = tripSummarySelectors.selectLiveChecksHaveExpired(
        state,
        allTripIds,
        lcOpts,
    );
    const showSingleBookButton = tripSummarySelectors.selectSingleBookButtonSupport(
        state,
        allTripIds,
        lcOpts,
    );
    const deeplinkRoundtrip = tripSummarySelectors.selectSingleDeeplinkBookButtonSupport(
        state,
        allTripIds,
        lcOpts,
    );

    const fareVersion = 'v2';

    // Search Data
    const {
        opid: originPlaceId,
        dpid: destinationPlaceId,
    } = searchQueries as {
        opid?: string;
        dpid?: string;
    };

    const urlDepartFilterParams = searchSelectors.selectUrlFilterParams(ownProps, DEPART_DIRECTION);
    const urlReturnFilterParams = searchSelectors.selectUrlFilterParams(ownProps, RETURN_DIRECTION);

    const tripFareNames = tripSummarySelectors.selectAvailableFareNames(
        state,
        tripItineraryId,
        seats,
        wheelchairs,
    ) || {};

    return {
        // Trip Data
        allTripIds,
        itineraryId,
        outboundArrive,
        outboundDepart,
        outboundTripError,
        outboundTripId,
        outboundTripIds,
        outboundTrips,
        returnDepart,
        returnTripError,
        returnTripId,
        returnTripIds,
        returnTrips,
        tripFareNames,
        tripItineraryId,
        tripPrices,

        // LiveCheck Data
        // eslint-disable-next-line sort-keys
        deeplinkRoundtrip,
        expiresAt: earliestExpiresAt,
        fareVersion,
        liveCheckData,
        liveChecksCompleted,
        liveChecksHaveAlreadyExpired,
        liveChecksInProgress,
        showSingleBookButton,

        // Seat Data
        // eslint-disable-next-line sort-keys
        requestedWheelchairs,
        seatInfo,
        seats,
        wheelchairs,

        // Search Data
        // eslint-disable-next-line sort-keys
        carrierProviderGroup,
        destinationPlaceId,
        originPlaceId,
        searchQueries,
        urlDepartFilterParams,
        urlReturnFilterParams,

        // Context
        // eslint-disable-next-line sort-keys
        accountEmail: userInfoSelectors.selectUserInfo(state)?.email,
        affiliateId: partnerDetails?.affiliateId,
        currency: selectCurrency(state),
        identityId: userInfoSelectors.selectIdentityId(state),
        locale: selectLocale(state),
        trackingId: partnerDetails?.trackingId,

        // eslint-disable-next-line sort-keys
        forceSessionReplay: splitTestSelectors.selectTestVariant(state, 'forceTripSummarySessionReplay'),
        showTotalPrice,
        tripsAreLoaded,
    };
};

const mapDispatchToProps = {
    fetchCarrierData,
    fetchTrip: fetchTripsAction.fetchTrip,
    fetchTripItinerary: fetchTripItineraryAction.fetchTripItinerary,
    liveCheckItinerary,
    refreshLiveCheckItinerary,
    removeTrip: removeTripsWithId,
    removeTripIds: tripSummaryActions.removeTripIds,
    resetLiveCheckItinerary: liveCheckItineraryAction.resetLiveCheckItinerary,
    resetTravelerInfo: tripSummaryActions.resetTravelerInfo,
    setDepartTripId: tripSummaryActions.setDepartTripId,
    setReturnTripId: tripSummaryActions.setReturnTripId,
    setTravelerInfo: tripSummaryActions.setTravelerInfo,
};

export {
    TripSummary,
    mapStateToProps,
};
export default connect(mapStateToProps, mapDispatchToProps)(TripSummary);
